import React, { useState, useRef } from 'react';
import { CheckCircle2Icon, StarIcon, ShieldCheckIcon, PlayIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
import { FadeIn } from './FadeIn';
export function HeroSection() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };
  return <section className="pt-14 pb-8 md:pt-32 md:pb-20 px-4 overflow-hidden relative bg-gradient-to-b from-white to-gray-50/50" data-id="element-162">
      <div className="max-w-4xl mx-auto relative z-10 flex flex-col items-center text-center" data-id="element-163">
        <FadeIn className="w-full flex flex-col items-center" data-id="element-164">
          <div className="inline-flex items-center gap-1 bg-gradient-to-r from-kid-yellow/20 to-kid-orange/20 px-4 py-1.5 rounded-full shadow-sm border border-kid-yellow/30 mb-5 md:mb-8" data-id="element-165">
            <div className="flex text-kid-yellow" data-id="element-166">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-current" data-id="element-167" />)}
            </div>
            <span className="text-xs md:text-sm font-medium text-gray-600 ml-2 tracking-wide" data-id="element-168">
              Padres Satisfechos
            </span>
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-4 md:mb-6 text-balance max-w-3xl tracking-tight" data-id="element-169">
            <span className="block mb-1 md:mb-2" data-id="element-170">
              El problema no es tu hijo.
            </span>
            <span className="block bg-gradient-to-r from-kid-purple to-kid-pink bg-clip-text text-transparent" data-id="element-171">
              Es el orden en que le enseñan a leer.
            </span>
          </h1>

          <div className="text-base md:text-lg text-gray-600 mb-5 md:mb-6 leading-relaxed max-w-2xl mx-auto font-normal" data-id="element-172">
            <p className="mb-1.5 md:mb-2" data-id="element-173">
              Descubrí la{' '}
              <span className="underline decoration-gray-300 underline-offset-4" data-id="element-174">
                secuencia correcta de aprendizaje
              </span>{' '}
              que conecta
            </p>
            <p className="text-2xl md:text-3xl font-semibold my-3 md:my-4 text-gray-800" data-id="element-175">
              <span className="text-kid-purple" data-id="element-176">sonido</span>{' '}
              <span className="text-gray-400 mx-1 md:mx-2" data-id="element-177">→</span>{' '}
              <span className="text-kid-pink" data-id="element-178">trazo</span>{' '}
              <span className="text-gray-400 mx-1 md:mx-2" data-id="element-179">→</span>{' '}
              <span className="text-kid-sky" data-id="element-180">palabra</span>
            </p>
            <p data-id="element-181">
              para transformar la frustración en{' '}
              <span className="font-semibold text-gray-900" data-id="element-182">
                avances reales
              </span>{' '}
              y ayudar a tu hijo a iniciar la lectura con más seguridad en{' '}
              <span className="font-semibold text-kid-purple bg-kid-purple/10 px-2 py-0.5 rounded-md" data-id="element-183">
                solo 10 minutos por día.
              </span>
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.2} className="w-full flex flex-col items-center" data-id="element-184">
          <ul className="space-y-2.5 md:space-y-3 mb-6 md:mb-10 max-w-md mx-auto w-full" data-id="element-185">
            {[{
            text: 'Método guiado paso a paso',
            color: 'text-kid-lime',
            bg: 'bg-kid-lime/10',
            border: 'border-kid-lime/20'
          }, {
            text: 'Material imprimible listo para usar',
            color: 'text-kid-sky',
            bg: 'bg-kid-sky/10',
            border: 'border-kid-sky/20'
          }, {
            text: 'Diseñado para niños de 3 a 7 años',
            color: 'text-kid-purple',
            bg: 'bg-kid-purple/10',
            border: 'border-kid-purple/20'
          }].map((item, idx) => <li key={idx} className={`flex items-center gap-3 text-gray-700 font-medium text-sm md:text-base p-2.5 md:p-3 rounded-xl ${item.bg} border ${item.border}`} data-id="element-186">
                <CheckCircle2Icon className={`w-5 h-5 ${item.color} flex-shrink-0`} data-id="element-187" />
                <span className="text-left" data-id="element-188">{item.text}</span>
              </li>)}
          </ul>
        </FadeIn>

        {/* Video Section */}
        <FadeIn delay={0.3} className="w-full mb-6 md:mb-10" data-id="element-189">
          <p className="text-lg md:text-xl font-semibold text-gray-800 mb-4" data-id="element-190">
            Mirá como funciona el sistema 👇🏻
          </p>
          <div className="relative w-full aspect-video max-w-lg mx-auto rounded-2xl overflow-hidden shadow-lg border border-gray-200 cursor-pointer" onClick={togglePlay} data-id="element-191">
            <video ref={videoRef} src="https://cdn.magicpatterns.com/uploads/1NRg38cf86cPB5WEhSHuS7/ssstik.io_@gloria.logopeda_1772127987243.mp4" className="w-full h-full object-cover" loop playsInline controlsList="nodownload nofullscreen noremoteplayback" disablePictureInPicture onContextMenu={e => e.preventDefault()} data-id="element-192" />

            {/* Thumbnail overlay with product image, play button, red arrow & CTA */}
            {!isPlaying && <div className="absolute inset-0 z-10" data-id="element-193">
                {/* Product image as thumbnail */}
                <img src="https://cdn.magicpatterns.com/uploads/amss8XA8X9Qmarc4J38DBE/20260307_1715_Image_Generation_simple_compose_01kk4yzbgweby8nda2gf500bht_(1).webp" alt="Sistema de Lectura en Orden Correcto" className="w-full h-full object-cover" data-id="element-194" />

                {/* Dark overlay for contrast */}
                <div className="absolute inset-0 bg-black/25" data-id="element-195" />

                {/* Play button - centered - VIOLET */}
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-196">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center shadow-2xl border-[3px] border-white" style={{
                backgroundColor: '#8B5CF6'
              }} data-id="element-197">
                    <PlayIcon className="w-7 h-7 md:w-9 md:h-9 text-white ml-1 fill-white" data-id="element-198" />
                  </div>
                </div>

                {/* Violet arrow + "HACÉ CLIC AQUÍ" text */}
                <div className="absolute bottom-3 right-3 md:bottom-5 md:right-5 flex flex-col items-center" data-id="element-199">
                  <span className="font-extrabold text-sm md:text-lg tracking-wide" style={{
                color: '#7C3AED',
                textShadow: '0 1px 4px rgba(255,255,255,0.95), 0 0 10px rgba(255,255,255,0.8), 0 2px 8px rgba(0,0,0,0.2)'
              }} data-id="element-200">
                    HACÉ CLIC AQUÍ
                  </span>

                  {/* Polished thick filled violet curved arrow */}
                  <svg width="110" height="90" viewBox="0 0 110 90" className="w-20 h-16 md:w-24 md:h-20 -mt-1" style={{
                filter: 'drop-shadow(0 3px 6px rgba(124,58,237,0.4))'
              }} data-id="element-201">
                    {/* Thick filled curved arrow body */}
                    <path d="M80 78 C76 50, 55 28, 30 22" stroke="#7C3AED" strokeWidth="10" strokeLinecap="round" fill="none" data-id="element-202" />
                    {/* Large filled solid arrowhead */}
                    <polygon points="8,6 32,14 18,30" fill="#7C3AED" stroke="#7C3AED" strokeWidth="2" strokeLinejoin="round" data-id="element-203" />
                  </svg>
                </div>
              </div>}
          </div>
        </FadeIn>

        <FadeIn delay={0.4} className="w-full flex flex-col items-center" data-id="element-204">
          <div className="flex flex-col items-center gap-3 md:gap-4 w-full max-w-md" data-id="element-205">
            <CTAButton text="QUIERO EL SISTEMA AHORA »" size="xl" className="w-full" animatePulse={true} data-id="element-206" />
            <div className="flex flex-wrap items-center justify-center gap-2 text-xs md:text-sm text-gray-500 font-medium" data-id="element-207">
              <ShieldCheckIcon className="w-4 h-4 text-kid-lime" data-id="element-208" />
              <span data-id="element-209">Acceso inmediato</span>
              <span className="text-gray-300" data-id="element-210">·</span>
              <span data-id="element-211">Pago único</span>
              <span className="text-gray-300" data-id="element-212">·</span>
              <span data-id="element-213">Sin suscripciones</span>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}